// `role` (on the user object / localStorage) is only ever "business_admin"
// (the org owner) or "business_user" (everyone invited) — it never tells you
// what an invited member can actually do. That's org_role instead
// (crm-pages/team/index.tsx's ORG_ROLES): admin, conversation_agent,
// crm_admin, campaign_manager, marketing_manager.
//
// Neither /auth/login nor /auth/get_userdata's typed response declares
// org_role, so this is read from whatever's cached — the dedicated
// "org_role" key (set at login, see auth/login.tsx, and refreshed by
// cloud-dashboard/index.tsx's /auth/get_userdata call) falling back to the
// raw "userDetails" blob for sessions that logged in before that caching
// existed (same file-local-JSON-parse pattern as
// update-customer.tsx's getStoredUserDefaults).
export const getStoredOrgRole = (): string | null => {
  const cached = localStorage.getItem("org_role");
  if (cached) return cached;
  try {
    const stored = localStorage.getItem("userDetails");
    return stored ? JSON.parse(stored)?.org_role || null : null;
  } catch {
    return null;
  }
};

// The three org_role values that get ConvoCRM access at all — the two
// marketing roles (campaign_manager, marketing_manager) stay Marketing-only.
export const CRM_ACCESS_ORG_ROLES = ["admin", "conversation_agent", "crm_admin"];

export const hasCrmAccess = (role: string, orgRole?: string | null): boolean =>
  role === "business_admin" || CRM_ACCESS_ORG_ROLES.includes(orgRole ?? getStoredOrgRole() ?? "");

// conversation_agent gets a stripped-down CRM Home/Leads experience (no
// QR-lead-capture widgets, no "recent leads" preview — just the paginated
// table) — see crm-pages/home and pages/leads for where this is used.
export const isConversationAgent = (orgRole?: string | null): boolean =>
  (orgRole ?? getStoredOrgRole()) === "conversation_agent";

// The mirror image of hasCrmAccess: conversation_agent is CRM-only, so the
// Marketing hub is blocked for it specifically (not for the other org_roles
// — crm_admin/admin/campaign_manager/marketing_manager all keep Marketing
// access same as before). `role` isn't needed here the way hasCrmAccess
// needs it for business_admin — the org owner is never org_role
// "conversation_agent", so no equivalent always-allow case applies.
export const hasMarketingAccess = (orgRole?: string | null): boolean =>
  !isConversationAgent(orgRole);
